import React, {FC, useCallback, useMemo, useState, useEffect, type JSX} from 'react';
import {LayoutChangeEvent, Platform, ScrollView, StyleSheet, View} from 'react-native';
import {KeyboardStickyView} from 'react-native-keyboard-controller';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {ColumnSize, QuizStickyFooter, Row, ScreenSize, useScreenSize} from '@lookiero/quiz';
import {Layout, LayoutProps} from '@components/layouts/layout/Layout';
import {HEADER_HEIGHT, style as quizLayoutStyle} from './QuizLayout.style';
import {inAppBrowser} from '@helpers';
import {TradeName} from '@domain/model/Config/Configuration';
import {ConfigurationApplication} from '@application/Config';

const tradename = ConfigurationApplication.retrieve().tradename;

interface QuizLayoutProps extends LayoutProps {
  readonly stepper?: JSX.Element;
  readonly size?: Partial<Record<ScreenSize, ColumnSize>>;
  readonly hideCard?: boolean;
}
const QuizLayout: FC<QuizLayoutProps> = ({
  children,
  stepper,
  footer,
  size = {M: '2/3', L: '1/3'},
  style: customStyle,
  scrollRef,
  hideCard = false,
  ...props
}) => {
  const {bottom: bottomInset} = useSafeAreaInsets();
  const style = useMemo(() => quizLayoutStyle(bottomInset), [bottomInset]);
  const isIOS = Platform.OS === 'ios';
  const [footerHeight, setFooterHeight] = useState(0);
  const handleFooterLayout = useCallback(
    (event: LayoutChangeEvent) => setFooterHeight(event.nativeEvent.layout.height),
    [],
  );

  const screenSize = useScreenSize();
  const contentContainerStyle = screenSize === 'S' ? style.contentContainerSmall : style.contentContainerLarge;
  const contentContainerSizeForScreenSize = size[screenSize];

  const shouldRenderStepper = stepper && !(tradename === TradeName.OUTFITTERY && screenSize !== 'S');
  const shouldRenderCard = tradename === TradeName.LOOKIERO && !hideCard;

  const stickyHeaderIndices = useMemo(() => {
    if (tradename === TradeName.LOOKIERO) {
      if (!shouldRenderCard) return [];
      return shouldRenderStepper ? [1] : [0];
    }

    if (tradename === TradeName.OUTFITTERY && shouldRenderStepper) {
      return [0];
    }

    return [];
  }, [shouldRenderStepper, shouldRenderCard]);

  useEffect(() => {
    if (inAppBrowser && Platform.OS === 'web') {
      /* Workaround to fix unexpected scroll behavior in FB in-app browser by forcing a resize calculation */
      window.dispatchEvent(new Event('resize'));
    }
  }, []);

  const stickyFooter = footer ? (
    <KeyboardStickyView style={style.stickyFooter}>
      <View onLayout={handleFooterLayout}>
        <QuizStickyFooter>{footer}</QuizStickyFooter>
      </View>
    </KeyboardStickyView>
  ) : null;

  if (inAppBrowser) {
    return (
      <>
        <Layout
          {...props}
          scrollRef={scrollRef}
          bounces={false}
          overScrollMode="never"
          style={{
            safeAreaView: customStyle?.safeAreaView,
            scrollView: [
              style.contentContainer,
              footer && {marginBottom: Math.max(footerHeight - 1, 0)},
              customStyle?.scrollView,
            ],
            header: [style.header, customStyle?.header],
          }}
        >
          {stepper && tradename === TradeName.LOOKIERO && <View style={style.stepper}>{stepper}</View>}

          <View style={[style.sticky, {top: HEADER_HEIGHT}]}>
            {stepper && tradename === TradeName.OUTFITTERY && <View style={style.stepper}>{stepper}</View>}

            {shouldRenderCard && (
              <View style={style.cardContainer}>
                <View style={style.card} />
              </View>
            )}
          </View>

          <View
            style={[
              style.children,
              tradename === TradeName.OUTFITTERY && style.content,
              {zIndex: tradename === TradeName.LOOKIERO ? 20 : 0},
              customStyle?.content,
            ]}
          >
            {children}
          </View>
        </Layout>
        {stickyFooter}
      </>
    );
  }

  return (
    <>
      <Layout
        {...props}
        edges={['top', 'left', 'right']}
        footer={null}
        style={{
          safeAreaView: [style.container, customStyle?.safeAreaView],
          scrollView: customStyle?.scrollView,
          header: customStyle?.header,
        }}
      >
        {stepper && screenSize !== 'S' && tradename === TradeName.OUTFITTERY && (
          <View style={style.stepper}>{stepper}</View>
        )}

        <Row style={style.row}>
          <ScrollView
            ref={scrollRef}
            showsVerticalScrollIndicator={false}
            scrollEventThrottle={16}
            bounces={false}
            overScrollMode="never"
            stickyHeaderIndices={stickyHeaderIndices}
            style={StyleSheet.absoluteFill}
            contentContainerStyle={{
              ...style.contentContainer,
              ...contentContainerStyle,
              ...(contentContainerSizeForScreenSize && style[contentContainerSizeForScreenSize]),
              ...(footer ? {marginBottom: Math.max(footerHeight - 1, 0)} : {}),
            }}
            keyboardShouldPersistTaps="handled"
            testID="quiz-layout-scroll-view"
            {...(!isIOS ? {bottomOffset: 10} : {})}
          >
            {shouldRenderStepper && <View style={style.stepper}>{stepper}</View>}

            {shouldRenderCard && (
              <View style={style.cardContainer}>
                <View style={style.card} />
              </View>
            )}

            <View
              style={[
                style.children,
                tradename === TradeName.OUTFITTERY && style.content,
                {zIndex: tradename === TradeName.LOOKIERO ? 20 : 0},
                customStyle?.content,
              ]}
            >
              {children}
            </View>
          </ScrollView>
        </Row>
      </Layout>
      {stickyFooter}
    </>
  );
};

export {QuizLayout};
